import { BellRingingIcon, CheckIcon, TrashIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { useBulkDeleteChecklists } from '@/api/checklists/bulk-delete-checklists'
import { useBulkSendReminderChecklists } from '@/api/checklists/bulk-send-reminder-checklists'
import { useBulkUpdateStatusChecklists } from '@/api/checklists/bulk-update-status-checklists'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { BulkAction } from '@/components/ui/table'
import { popupFactory } from '@/factories'
import { Checklist } from '@/models/features'
import { Building } from '@/reducers/types'

import { ChecklistStatusExtended } from './checklists-dashboard.page.types'
import {
  getChecklistBulkStatusAction,
  getChecklistBulkStatusConfirmationText,
  getChecklistBulkStatuses,
} from './checklists-dashboard.page.utils'

interface UseChecklistsBulkActionsProps {
  building?: Building | null
  statusTab?: ChecklistStatusExtended
}

export const useChecklistsBulkActions = ({
  building,
  statusTab,
}: UseChecklistsBulkActionsProps): BulkAction<Checklist>[] => {
  const { t } = useTranslation(['checklists', 'common'])

  const { mutateAsync: bulkDeleteChecklists } = useBulkDeleteChecklists()
  const { mutateAsync: bulkSendReminderChecklists } = useBulkSendReminderChecklists()
  const { mutateAsync: bulkUpdateStatusChecklists } = useBulkUpdateStatusChecklists()

  const bulkStatusActions: BulkAction<Checklist>[] = getChecklistBulkStatuses(statusTab).map(
    (status) => ({
      label: getChecklistBulkStatusAction(status, t).label,
      onClick: (items) => {
        if (!building) return

        popupFactory.open(ConfirmationPopup, {
          children: getChecklistBulkStatusConfirmationText(status, items.length, t),
          confirmIcon: CheckIcon,
          onConfirm: async () => {
            await bulkUpdateStatusChecklists({
              buildingId: building.id,
              checklistIds: items.map((item) => item.id),
              status,
            })
          },
          title: 'Change status',
          variant: 'info',
        })
      },
    }),
  )

  return [
    {
      icon: TrashIcon,
      onClick: (items) => {
        if (!building) return

        popupFactory.open(ConfirmationPopup, {
          children: t('checklists:dashboard.bulkActions.delete.confirmBody', {
            itemsCount: items.length,
          }),
          confirmText: t('common:actions.delete'),
          onConfirm: async () => {
            await bulkDeleteChecklists({
              buildingId: building.id,
              checklistIds: items.map((item) => item.id),
            })
          },
          title: t('checklists:dashboard.bulkActions.delete.confirmTitle'),
        })
      },
      onlyBox: true,
      use: 'danger',
    },
    {
      icon: BellRingingIcon,
      label: t('checklists:dashboard.bulkActions.sendReminder.label'),
      onClick: (items) => {
        if (!building) return

        popupFactory.open(ConfirmationPopup, {
          children: t('checklists:dashboard.bulkActions.sendReminder.confirmBody'),
          confirmText: t('checklists:dashboard.bulkActions.sendReminder.confirmText'),
          onConfirm: async () => {
            await bulkSendReminderChecklists({
              buildingId: building.id,
              checklistIds: items.map((item) => item.id),
            })
          },
          title: t('checklists:dashboard.bulkActions.sendReminder.confirmTitle'),
          variant: 'warning',
        })
      },
    },
    ...bulkStatusActions,
  ]
}
